<section id="enterprise" class="bg-white py-24 sm:py-32">
  <div class="mx-auto flex max-w-page flex-col gap-16 px-4 sm:px-6 lg:gap-20 lg:px-8">

    <!-- Header -->
    <div class="mx-auto flex max-w-4xl flex-col gap-6 text-center">
      <p class="text-caption font-semibold uppercase tracking-[0.2em] text-slate-500">
        <span class="text-primary-600">●</span>
        The enterprise way
      </p>

      <h2 class="text-h2 font-medium text-slate-950">
        Built for the most complex form operations
      </h2>
    </div>

    <!-- Two-column body -->
    <div class="grid gap-12 lg:grid-cols-[0.42fr_0.58fr] lg:items-stretch lg:gap-16">

      <!-- Left: steps -->
      <div class="flex flex-col gap-10">
        <div class="flex flex-col gap-4">
          <div class="flex items-center gap-3">
            <h3 class="text-h5 text-slate-950">Form Builder</h3>
          </div>

          <p class="max-w-md text-body1 leading-8 text-slate-600">
            A low-code environment that lets you build, validate, and publish
            enterprise form workflows fast.
          </p>
        </div>

        <div class="border-t border-slate-200">
          @for (step of steps; track step.id; let last = $last) {
            <div class="relative" [class.border-b]="!last" [class.border-slate-200]="!last">
              <div class="step-loader-bar">
                <div
                  class="step-loader-fill"
                  [class.is-active]="isAnimatingStep(step.id)"></div>
              </div>

              <button
                type="button"
                (click)="setActiveStep(step.id)"
                class="w-full py-6 text-left transition">
                <div class="flex items-start gap-4">
                  <span
                    class="text-body1 font-semibold tabular-nums"
                    [class.text-primary-600]="isActiveStep(step.id)"
                    [class.text-slate-400]="!isActiveStep(step.id)">
                    {{ step.number }}
                  </span>

                  <div class="min-w-0 flex-1">
                    <p
                      class="text-body1"
                      [class.font-semibold]="isActiveStep(step.id)"
                      [class.text-primary-600]="isActiveStep(step.id)"
                      [class.text-slate-500]="!isActiveStep(step.id)">
                      {{ step.title }}
                    </p>
                  </div>
                </div>
              </button>

              <div
                class="step-accordion-body"
                [class.is-open]="isActiveStep(step.id)">
                <div class="step-accordion-inner flex flex-col gap-4">
                  <p class="text-body2 leading-7 text-slate-600">
                    {{ step.description }}
                  </p>
                  <a
                    routerLink="/use-cases"
                    class="flex items-center gap-1.5 pb-6 text-body2">
                    <span class="text-primary-600 hover:text-primary-700">Learn more</span>
                    <app-svg-icon class="text-primary-600 hover:text-primary-700" src="assets/icons/arrow-right.svg" [size]="18"></app-svg-icon>
                  </a>
                </div>
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Right: visual mock -->
      <div
        class="relative h-full min-h-[22rem] overflow-hidden rounded-[2rem] bg-cover bg-center"
        style="background-image: url('../../../../../assets/images/landing/mountain-2.webp');">
        <div class="absolute inset-0 bg-slate-950/5"></div>

        <div class="relative flex h-full min-h-[22rem] items-center justify-center p-4 sm:p-6">
          <div class="w-full max-w-md rounded-2xl border border-white/60 bg-white/85 p-4 shadow-2xl shadow-slate-950/20 backdrop-blur sm:p-6">

            @if (isActiveStep('create')) {
              <div class="mock-step-enter flex flex-col gap-4">
              <p class="text-body2 font-semibold text-slate-950">Create new form</p>

              <div class="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-3">
                <div class="flex justify-end">
                  <p class="max-w-[85%] rounded-2xl rounded-br-md bg-primary-600 px-3 py-2 text-caption text-white">
                    Create a vendor onboarding survey
                  </p>
                </div>

                <div class="flex items-start gap-2">
                  <span
                    class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-caption font-semibold text-secondary-700">
                    AI
                  </span>
                  <p
                    class="rounded-2xl rounded-bl-md border border-slate-200 bg-white px-3 py-2 text-caption leading-5 text-slate-700">
                    I'll add name, email, and department fields. Want a file upload too?
                  </p>
                </div>

                <div class="rounded-full border border-slate-200 bg-white px-3 py-2 text-caption text-slate-400">
                  Ask AI to add or edit fields...
                </div>
              </div>

              <div class="flex flex-col gap-3">
                <div class="flex flex-col gap-1 rounded-xl border border-slate-300 bg-white p-3">
                  <p class="text-caption text-slate-500">Full name</p>
                  <div class="h-9 rounded-lg bg-slate-100"></div>
                </div>

                <div class="flex flex-col gap-1 rounded-xl border border-slate-200 bg-white p-3">
                  <p class="text-caption text-slate-500">Work email</p>
                  <div class="h-9 rounded-lg bg-slate-100"></div>
                </div>
              </div>
              </div>
            } @else if (isActiveStep('logic')) {
              <div class="mock-step-enter flex flex-col gap-4">
              <p class="text-body2 font-semibold text-slate-950">Configure logic</p>

              <div class="flex flex-col gap-1">
                <p class="text-body3 font-semibold text-slate-950">Field validations</p>
                <p class="text-caption text-slate-500">
                  Set rules for each form field
                </p>
              </div>

              <div class="flex flex-col gap-1 rounded-xl border border-primary-200 bg-white p-3 ring-1 ring-primary-100">
                <p class="text-caption text-slate-500">Work email</p>
                <div class="h-9 rounded-lg bg-slate-100"></div>
              </div>

              <div class="flex flex-col gap-2">
                <p class="text-caption text-slate-500">Validations</p>
                <div class="flex flex-wrap gap-2">
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full border border-error-200 bg-white py-1 pl-1 pr-3 shadow-sm">
                    <span
                      class="flex h-6 w-6 items-center justify-center rounded-full bg-error-100 text-caption font-bold text-error-600">
                      *
                    </span>
                    <span class="text-caption font-semibold text-slate-900">Required</span>
                  </span>
                  <span
                    class="inline-flex items-center gap-1.5 rounded-full border border-primary-200 bg-white py-1 pl-1 pr-3 shadow-sm">
                    <span class="flex h-6 w-6 items-center justify-center rounded-full bg-primary-100">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-3 w-3 text-primary-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        stroke-width="2">
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </span>
                    <span class="text-caption font-semibold text-slate-900">Email</span>
                  </span>
                </div>
              </div>
              </div>
            } @else if (isActiveStep('workflow')) {
              <div class="mock-step-enter flex flex-col gap-5">
              <p class="text-body2 font-semibold text-slate-950">Configure workflow</p>

              <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-1">
                  <p class="text-body3 font-semibold text-slate-950">Workflow actions</p>
                  <p class="text-caption text-slate-500">
                    Trigger automations when a form is submitted
                  </p>
                </div>

                <div class="flex gap-3">
                  <div class="flex flex-col items-center pt-4">
                    <span class="h-2 w-2 shrink-0 rounded-full bg-primary-500"></span>
                    <div class="w-px grow bg-slate-200"></div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      class="h-3 w-3 shrink-0 text-slate-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                    <div class="w-px grow bg-slate-200"></div>
                  </div>

                  <div class="flex min-w-0 flex-1 flex-col gap-3">
                    <div
                      class="flex items-center gap-3 rounded-xl border border-secondary-200 bg-white px-3 py-2.5 shadow-sm">
                      <span
                        class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          stroke-width="2">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                      </span>
                      <div class="min-w-0">
                        <p class="text-caption font-semibold text-slate-900">Webhook trigger</p>
                        <p class="text-[11px] text-slate-500">On form submission</p>
                      </div>
                    </div>

                    <div
                      class="flex items-center gap-3 rounded-xl border border-primary-200 bg-white px-3 py-2.5 shadow-sm">
                      <span class="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary-100">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-4 w-4 text-primary-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          stroke-width="2">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </span>
                      <div class="min-w-0">
                        <p class="text-caption font-semibold text-slate-900">Send email</p>
                        <p class="text-[11px] text-slate-500">Notify vendor team</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            } @else if (isActiveStep('deploy')) {
              <div class="mock-step-enter flex flex-col gap-4">
              <div class="flex items-center justify-between gap-3">
                <p class="text-body2 font-semibold text-slate-950">Deploy and integrate</p>
                <span class="rounded-full bg-success-100 px-3 py-1 text-caption font-medium text-success-700">
                  Live
                </span>
              </div>

              <button
                type="button"
                class="w-full rounded-xl bg-primary-600 px-4 py-2.5 text-caption font-semibold text-white shadow-sm">
                Publish form
              </button>

              <div class="flex flex-col gap-4">
                <div class="flex flex-col gap-2">
                  <p class="text-body3 font-semibold text-slate-950">Public link</p>
                  <p class="truncate rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-body2 font-medium text-primary-600">
                    formops.app/f/vendor-onboarding
                  </p>
                </div>

                <div class="flex flex-col gap-2">
                  <p class="text-body3 font-semibold text-slate-950">Embed link</p>
                  <p class="truncate rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-caption font-medium text-slate-600">
                    &lt;iframe src="formops.app/embed/vendor-onboarding"&gt;&lt;/iframe&gt;
                  </p>
                </div>
              </div>
              </div>
            } @else if (isActiveStep('insights')) {
              <div class="mock-step-enter flex flex-col gap-5">
              <div class="flex items-center justify-between gap-3">
                <p class="text-body2 font-semibold text-slate-950">Data insights</p>
                <span class="rounded-full bg-secondary-100 px-3 py-1 text-caption font-medium text-secondary-700">
                  Live
                </span>
              </div>

              <div class="grid grid-cols-2 gap-3">
                <div class="relative flex flex-col gap-2 overflow-hidden rounded-2xl bg-primary-950 p-4 text-white">
                  <div class="absolute -right-6 -top-6 h-20 w-20 rounded-full bg-primary-500/30 blur-2xl"></div>
                  <p class="relative text-caption text-primary-200">Submissions</p>
                  <p class="relative text-body2 font-semibold text-white">1,248 responses collected</p>
                  <p class="relative text-[11px] leading-4 text-primary-100">
                    Track response trends, completion status, and form performance.
                  </p>
                </div>

                <div class="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <div class="flex items-center justify-between gap-2">
                    <p class="text-caption font-semibold text-secondary-600">AI Insight</p>
                    <span class="rounded-full bg-secondary-100 px-2 py-0.5 text-[11px] font-medium text-secondary-700">
                      New
                    </span>
                  </div>
                  <p class="text-body2 font-semibold text-slate-950">Feedback trend detected</p>
                  <p class="text-[11px] leading-4 text-slate-600">
                    Most users mention faster onboarding and cleaner form experience.
                  </p>
                  <div class="flex h-12 items-end gap-1.5">
                    <div class="h-5 flex-1 rounded-t-lg bg-secondary-200"></div>
                    <div class="h-7 flex-1 rounded-t-lg bg-secondary-300"></div>
                    <div class="h-6 flex-1 rounded-t-lg bg-secondary-200"></div>
                    <div class="h-9 flex-1 rounded-t-lg bg-secondary-400"></div>
                    <div class="h-10 flex-1 rounded-t-lg bg-secondary-500"></div>
                  </div>
                </div>
              </div>
              </div>
            }

          </div>
        </div>
      </div>

    </div>
  </div>
</section>
